// ###### IMPORTS
import React, { useMemo } from 'react';
import { BanknotesIcon } from '@heroicons/react/24/solid';
import SpinnerLoad from 'views/finance/moduleComponents/SpinnerLoad';
import { formatDate } from 'shared_items/formatConventions';
import { useAssetsData } from 'views/finance/moduleServices/assets_data_context';
import { computeAssetBalanceSummary } from 'views/finance/tab_assets/viewServices/assets_balance_summary';
import {
  ASSETS_CATEGORY_COLORS,
  buildSnapshotMapWithMarketValue,
  formatCompactCurrencyValue,
  sumSnapshotValueFromMap,
  sumMarketValueFromMap,
} from 'views/finance/tab_assets/viewServices/assets_helpers';
import { readAssetsBoardState } from 'views/finance/tab_assets/viewServices/assets_board_storage';
import { buildHierarchy, sortedLevel1Keys } from 'views/finance/tab_assets/viewComponents/Detailed/CardAssetsDetailed';

// ###### MAIN COMPONENT
const CardOverviewAssets = ({ onNavigateToAssets }) => {
  const {
    assets,
    categories,
    snapshot_items: snapshotItems,
    snapshots,
    loading,
    error: ctxError,
  } = useAssetsData();

  const summary = useMemo(
    () => computeAssetBalanceSummary({ snapshots, snapshot_items: snapshotItems, assets }, {}),
    [snapshots, snapshotItems, assets]
  );
  const latestSnapshot = summary.latest_snapshot;

  const snapshotMap = useMemo(() => {
    if (!latestSnapshot?.id) return {};
    const items = snapshotItems.filter((i) => i.snapshot === latestSnapshot.id);
    return buildSnapshotMapWithMarketValue(items);
  }, [latestSnapshot, snapshotItems]);

  const { valueMode } = readAssetsBoardState();

  const total = useMemo(() => {
    if (!assets.length) return 0;
    return valueMode === 'market'
      ? sumMarketValueFromMap(assets, snapshotMap)
      : sumSnapshotValueFromMap(assets, snapshotMap);
  }, [assets, snapshotMap, valueMode]);

  const byL0 = useMemo(() => {
    const hierarchy = buildHierarchy(assets, categories);
    const sortedL1 = sortedLevel1Keys(hierarchy);
    const sumRow = (assetList) =>
      valueMode === 'market'
        ? sumMarketValueFromMap(assetList, snapshotMap)
        : sumSnapshotValueFromMap(assetList, snapshotMap);
    const list = sortedL1
      .map((l1) => {
        const assetsInL1 = Object.keys(hierarchy[l1] || {}).flatMap((l2) => hierarchy[l1][l2] || []);
        const value = sumRow(assetsInL1);
        return { name: l1, value };
      })
      .filter((x) => x.value > 0);
    const totalSum = list.reduce((s, x) => s + x.value, 0);
    return list
      .map((x) => ({ ...x, percentage: totalSum > 0 ? (x.value / totalSum) * 100 : 0 }))
      .sort((a, b) => b.percentage - a.percentage);
  }, [assets, categories, snapshotMap, valueMode]);

  const hasData = !loading && latestSnapshot && total > 0;
  const error = ctxError;

  return (
    <div
      className={`bg-white shadow rounded-xl overflow-hidden ${onNavigateToAssets ? 'cursor-pointer hover:shadow-lg transition-shadow duration-200' : ''}`}
      onClick={() => onNavigateToAssets?.()}
      role={onNavigateToAssets ? 'button' : undefined}
      tabIndex={onNavigateToAssets ? 0 : undefined}
      onKeyDown={(e) => {
        if (!onNavigateToAssets) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onNavigateToAssets();
        }
      }}
    >
      <div className="bg-green-800 border-b border-gray-200">
        <div className="px-3 py-1 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <BanknotesIcon className="h-5 w-5 text-white" />
            <h2 className="text-base font-semibold text-white capitalize">
              Assets
            </h2>
          </div>

          <div className="flex items-center gap-2">
            <div className="text-xs text-white opacity-70">
              {latestSnapshot?.snapshot_date ? `snapshot: ${formatDate(latestSnapshot.snapshot_date, 'display')}` : 'snapshot: —'}
            </div>
          </div>
        </div>
      </div>

      <div className="h-48 p-3 relative">
        {loading && (
          <>
            <div className="animate-pulse h-8 w-24 bg-gray-200 rounded mb-3" />
            <div className="flex gap-3 items-stretch w-full" style={{ height: 140 }}>
              <div className="w-16 shrink-0 rounded overflow-hidden bg-gray-200 animate-pulse" />
              <div className="flex-1 min-w-0 flex flex-col gap-1">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="animate-pulse h-5 bg-gray-200 rounded flex-shrink-0" style={{ width: `${100 - i * 15}%` }} />
                ))}
              </div>
            </div>
            <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-70">
              <SpinnerLoad />
            </div>
          </>
        )}

        {!loading && error && (
          <div className="h-full flex items-center justify-center">
            <div className="text-red-500 text-sm">{error}</div>
          </div>
        )}

        {!loading && !error && (!latestSnapshot || Object.keys(snapshotMap).length === 0) && (
          <div className="h-full flex items-center justify-center">
            <span className="text-gray-400 text-sm">No snapshots</span>
          </div>
        )}

        {!loading && !error && latestSnapshot && (
          <div className="grid grid-cols-2 gap-4 h-full w-full items-center justify-items-center">
            <div className="flex flex-col items-center justify-center w-full h-full min-h-[120px]">
              <p className="text-2xl font-bold text-green-800 text-center leading-tight">
                R$ {formatCompactCurrencyValue(total)}
              </p>
              <span className="mt-1 text-sm font-medium text-green-800">
                {valueMode === 'market' ? 'market value' : 'base value'}
              </span>
            </div>
            <div className="flex items-center justify-center w-full h-full min-h-[120px]">
              {hasData && byL0.length > 0 ? (
                <div className="flex gap-3 items-stretch relative" style={{ height: 140 }}>
                  <div className="flex flex-col w-16 shrink-0 rounded overflow-hidden border border-gray-200">
                    {byL0.map((item, index) => (
                      <div
                        key={item.name}
                        className="flex items-center justify-center flex-shrink-0"
                        style={{
                          height: `${item.percentage}%`,
                          backgroundColor: ASSETS_CATEGORY_COLORS[index % ASSETS_CATEGORY_COLORS.length],
                        }}
                      >
                        <span className="text-white text-[10px] font-semibold">{Math.round(item.percentage * 10) / 10}%</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex flex-col flex-1 min-w-0">
                    {byL0.map((item) => (
                      <div
                        key={item.name}
                        className="flex items-center pl-2 flex-shrink-0 text-gray-800 text-xs"
                        style={{ height: `${item.percentage}%` }}
                        title={item.name}
                      >
                        <span className="truncate">{item.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <span className="text-gray-400 text-xs text-center px-2">No data</span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CardOverviewAssets;
